import type { Program, Athlete } from '@/types'

// Auto-converted from '7-week-pre-usa-program.xlsx' to the app's program format.
// Demonstrates a real coach-authored program rendered in the app's structure.
export const preUsaProgram: Program = {
  "id": "pre-usa-7wk",
  "name": "7-Week Pre-USA Program",
  "description": "Bilateral patellar tendinopathy + historical left MCL. Settle tendons, robust calves, frontal-plane control, expressible power before US departure.",
  "goal_tags": [
    "rehab",
    "tendon",
    "basketball",
    "power"
  ],
  "source": "import",
  "overview": {
    "presentation": "Male basketball athlete, 16 (turning 17). Bilateral patellar tendinopathy; historical left MCL sprain.",
    "goal": "Arrive in the US with settled tendons, robust calves, better frontal-plane control, and expressible power.",
    "frequency": "3 full-body sessions/week (A/B/C) + optional low-CNS Session D. Daily tendon isometrics 5–6 days/week.",
    "rules": [
      "Pain during loading up to 4/10 is acceptable — must return to baseline within 24h.",
      "Morning stiffness is the truth-teller: stiffer than last week = repeat the week, do not progress.",
      "Never train through sharp, localised on-the-tendon pain that changes mechanics.",
      "Court volume counts as load. Playing 5 days/week → sit at the LOW end of every range."
    ]
  },
  "weeks": [
    {
      "week": 1,
      "phase": "PHASE 1 — CALM & CAPACITY · RPE 6–7",
      "note": "Build the daily isometric habit. Isometrics + heavy-slow-resistance. No gym plyos yet.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 4,
              "reps": "8",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 4,
              "reps": "12",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 4,
              "reps": "10",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "20s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 4,
              "reps": "12/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 4,
              "reps": "8",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 3,
              "reps": "12/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 4,
              "reps": "8",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 3,
              "reps": "20",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 3,
              "reps": "8/side",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 4,
              "reps": "10/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "nordic-curl-assisted-band-or-razor-curl",
              "name": "Nordic curl (assisted/band) or razor curl",
              "sets": 3,
              "reps": "5",
              "notes": "Eccentric focus. OMIT in Week 7.",
              "group": "C1"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "10/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 3,
              "reps": "30m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 3,
              "reps": "8/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session C",
          "focus": "Unilateral + Frontal Plane",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "rear-foot-elevated-split-squat",
              "name": "Rear-foot elevated split squat",
              "sets": 4,
              "reps": "8/side",
              "tempo": "3-1-1",
              "notes": "Front shin travels - this is a knee-dominant variation on purpose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise-heavy",
              "name": "Seated calf raise - HEAVY",
              "sets": 4,
              "reps": "10",
              "notes": "3s eccentric. Heaviest calf exposure of the week.",
              "group": "A2"
            },
            {
              "exercise_id": "lateral-step-down-15-25cm-box",
              "name": "Lateral step-down (15-25cm box)",
              "sets": 3,
              "reps": "8/side",
              "notes": "Slow, controlled. Frontal-plane control - the MCL work.",
              "group": "B1"
            },
            {
              "exercise_id": "incline-db-press",
              "name": "Incline DB press",
              "sets": 4,
              "reps": "10",
              "notes": "45 deg bench.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-calf-raise-double-leg-burnout",
              "name": "Standing calf raise, double leg - burnout",
              "sets": 3,
              "reps": "20",
              "notes": "Blood flow / volume finisher. Full ROM, no bouncing.",
              "group": "C1"
            },
            {
              "exercise_id": "seated-cable-row-face-pulls-superset",
              "name": "Seated cable row + face pulls (superset)",
              "sets": 3,
              "reps": "12 + 3 x 15",
              "notes": "Upper back + posterior shoulder.",
              "group": "C2"
            },
            {
              "exercise_id": "side-plank",
              "name": "Side plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Anti-lateral flexion.",
              "group": "D1"
            },
            {
              "exercise_id": "ab-wheel-or-body-saw",
              "name": "Ab wheel or body saw",
              "sets": 3,
              "reps": "8",
              "notes": "Anti-extension. Stop the set when the low back gives.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session D",
          "focus": "Optional Low-CNS",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "daily-tendon-protocol-full",
              "name": "Daily tendon protocol (full)",
              "reps": "as prescribed",
              "notes": "See 'Daily Isometrics' tab.",
              "group": "1"
            },
            {
              "exercise_id": "leg-press-calf-raise",
              "name": "Leg press calf raise",
              "sets": 4,
              "reps": "15",
              "notes": "2s pause at the top.",
              "group": "2"
            },
            {
              "exercise_id": "single-leg-standing-calf-raise-bodyweigh",
              "name": "Single-leg standing calf raise (bodyweight)",
              "sets": 3,
              "reps": "15/side",
              "notes": "High quality only - stop when ROM drops.",
              "group": "3"
            },
            {
              "exercise_id": "tibialis-raise",
              "name": "Tibialis raise",
              "sets": 3,
              "reps": "25",
              "group": "4"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "group": "5"
            },
            {
              "exercise_id": "push-ups-and-band-pull-aparts",
              "name": "Push-ups AND band pull-aparts",
              "reps": "3 sets + 3 x 20",
              "notes": "Push-ups: AMRAP minus 2.",
              "group": "6"
            },
            {
              "exercise_id": "farmer-carry",
              "name": "Farmer carry",
              "sets": 3,
              "reps": "40m",
              "group": "7"
            }
          ]
        }
      ]
    },
    {
      "week": 2,
      "phase": "PHASE 1 — CALM & CAPACITY · RPE 6–7",
      "note": "Build the daily isometric habit. Isometrics + heavy-slow-resistance. No gym plyos yet.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 4,
              "reps": "8",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 4,
              "reps": "12",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 4,
              "reps": "10",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "20s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 4,
              "reps": "12/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 4,
              "reps": "8",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 3,
              "reps": "12/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 4,
              "reps": "8",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 3,
              "reps": "20",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 3,
              "reps": "8/side",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 4,
              "reps": "10/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "nordic-curl-assisted-band-or-razor-curl",
              "name": "Nordic curl (assisted/band) or razor curl",
              "sets": 3,
              "reps": "5",
              "notes": "Eccentric focus. OMIT in Week 7.",
              "group": "C1"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "10/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 3,
              "reps": "30m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 3,
              "reps": "8/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session C",
          "focus": "Unilateral + Frontal Plane",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "rear-foot-elevated-split-squat",
              "name": "Rear-foot elevated split squat",
              "sets": 4,
              "reps": "8/side",
              "tempo": "3-1-1",
              "notes": "Front shin travels - this is a knee-dominant variation on purpose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise-heavy",
              "name": "Seated calf raise - HEAVY",
              "sets": 4,
              "reps": "10",
              "notes": "3s eccentric. Heaviest calf exposure of the week.",
              "group": "A2"
            },
            {
              "exercise_id": "lateral-step-down-15-25cm-box",
              "name": "Lateral step-down (15-25cm box)",
              "sets": 3,
              "reps": "8/side",
              "notes": "Slow, controlled. Frontal-plane control - the MCL work.",
              "group": "B1"
            },
            {
              "exercise_id": "incline-db-press",
              "name": "Incline DB press",
              "sets": 4,
              "reps": "10",
              "notes": "45 deg bench.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-calf-raise-double-leg-burnout",
              "name": "Standing calf raise, double leg - burnout",
              "sets": 3,
              "reps": "20",
              "notes": "Blood flow / volume finisher. Full ROM, no bouncing.",
              "group": "C1"
            },
            {
              "exercise_id": "seated-cable-row-face-pulls-superset",
              "name": "Seated cable row + face pulls (superset)",
              "sets": 3,
              "reps": "12 + 3 x 15",
              "notes": "Upper back + posterior shoulder.",
              "group": "C2"
            },
            {
              "exercise_id": "side-plank",
              "name": "Side plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Anti-lateral flexion.",
              "group": "D1"
            },
            {
              "exercise_id": "ab-wheel-or-body-saw",
              "name": "Ab wheel or body saw",
              "sets": 3,
              "reps": "8",
              "notes": "Anti-extension. Stop the set when the low back gives.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session D",
          "focus": "Optional Low-CNS",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "daily-tendon-protocol-full",
              "name": "Daily tendon protocol (full)",
              "reps": "as prescribed",
              "notes": "See 'Daily Isometrics' tab.",
              "group": "1"
            },
            {
              "exercise_id": "leg-press-calf-raise",
              "name": "Leg press calf raise",
              "sets": 4,
              "reps": "15",
              "notes": "2s pause at the top.",
              "group": "2"
            },
            {
              "exercise_id": "single-leg-standing-calf-raise-bodyweigh",
              "name": "Single-leg standing calf raise (bodyweight)",
              "sets": 3,
              "reps": "15/side",
              "notes": "High quality only - stop when ROM drops.",
              "group": "3"
            },
            {
              "exercise_id": "tibialis-raise",
              "name": "Tibialis raise",
              "sets": 3,
              "reps": "25",
              "group": "4"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "group": "5"
            },
            {
              "exercise_id": "push-ups-and-band-pull-aparts",
              "name": "Push-ups AND band pull-aparts",
              "reps": "3 sets + 3 x 20",
              "notes": "Push-ups: AMRAP minus 2.",
              "group": "6"
            },
            {
              "exercise_id": "farmer-carry",
              "name": "Farmer carry",
              "sets": 3,
              "reps": "40m",
              "group": "7"
            }
          ]
        }
      ]
    },
    {
      "week": 3,
      "phase": "PHASE 2 — LOAD & STRENGTH · RPE 7–8.5",
      "note": "Heavy slow resistance, unilateral + frontal plane. Progress load ONLY if morning stiffness stayed flat.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "Ankle pogos (low, stiff, double leg) 4 x 15. Line hops 3 x 20s.",
              "notes": "Stiff ankle, minimal knee bend. Quiet contacts.",
              "group": "PLYO"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 4,
              "reps": "6 (heavier)",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 4,
              "reps": "10 heavy",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 4,
              "reps": "8",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 4,
              "reps": "10/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 4,
              "reps": "6-8",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 3,
              "reps": "12/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 4,
              "reps": "6",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 3,
              "reps": "25",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 3,
              "reps": "8/side heavier",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 4,
              "reps": "8/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "nordic-curl-assisted-band-or-razor-curl",
              "name": "Nordic curl (assisted/band) or razor curl",
              "sets": 3,
              "reps": "6",
              "notes": "Eccentric focus. OMIT in Week 7.",
              "group": "C1"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "8/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 3,
              "reps": "30m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 3,
              "reps": "10/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session C",
          "focus": "Unilateral + Frontal Plane",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "Ankle pogos (low, stiff, double leg) 4 x 15. Line hops 3 x 20s.",
              "notes": "Stiff ankle, minimal knee bend. Quiet contacts.",
              "group": "PLYO"
            },
            {
              "exercise_id": "rear-foot-elevated-split-squat",
              "name": "Rear-foot elevated split squat",
              "sets": 4,
              "reps": "6/side loaded",
              "tempo": "3-1-1",
              "notes": "Front shin travels - this is a knee-dominant variation on purpose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise-heavy",
              "name": "Seated calf raise - HEAVY",
              "sets": 4,
              "reps": "8 heaviest",
              "notes": "3s eccentric. Heaviest calf exposure of the week.",
              "group": "A2"
            },
            {
              "exercise_id": "lateral-step-down-15-25cm-box",
              "name": "Lateral step-down (15-25cm box)",
              "sets": 3,
              "reps": "10/side",
              "notes": "Slow, controlled. Frontal-plane control - the MCL work.",
              "group": "B1"
            },
            {
              "exercise_id": "incline-db-press",
              "name": "Incline DB press",
              "sets": 4,
              "reps": "8",
              "notes": "45 deg bench.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-calf-raise-double-leg-burnout",
              "name": "Standing calf raise, double leg - burnout",
              "sets": 3,
              "reps": "20",
              "notes": "Blood flow / volume finisher. Full ROM, no bouncing.",
              "group": "C1"
            },
            {
              "exercise_id": "seated-cable-row-face-pulls-superset",
              "name": "Seated cable row + face pulls (superset)",
              "sets": 3,
              "reps": "12 + 3 x 15",
              "notes": "Upper back + posterior shoulder.",
              "group": "C2"
            },
            {
              "exercise_id": "side-plank",
              "name": "Side plank",
              "sets": 3,
              "reps": "40s/side",
              "notes": "Anti-lateral flexion.",
              "group": "D1"
            },
            {
              "exercise_id": "ab-wheel-or-body-saw",
              "name": "Ab wheel or body saw",
              "sets": 3,
              "reps": "10",
              "notes": "Anti-extension. Stop the set when the low back gives.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session D",
          "focus": "Optional Low-CNS",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "daily-tendon-protocol-full",
              "name": "Daily tendon protocol (full)",
              "reps": "as prescribed",
              "notes": "See 'Daily Isometrics' tab.",
              "group": "1"
            },
            {
              "exercise_id": "leg-press-calf-raise",
              "name": "Leg press calf raise",
              "sets": 4,
              "reps": "15",
              "notes": "2s pause at the top.",
              "group": "2"
            },
            {
              "exercise_id": "single-leg-standing-calf-raise-bodyweigh",
              "name": "Single-leg standing calf raise (bodyweight)",
              "sets": 3,
              "reps": "15/side",
              "notes": "High quality only - stop when ROM drops.",
              "group": "3"
            },
            {
              "exercise_id": "tibialis-raise",
              "name": "Tibialis raise",
              "sets": 3,
              "reps": "25",
              "group": "4"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "group": "5"
            },
            {
              "exercise_id": "push-ups-and-band-pull-aparts",
              "name": "Push-ups AND band pull-aparts",
              "reps": "3 sets + 3 x 20",
              "notes": "Push-ups: AMRAP minus 2.",
              "group": "6"
            },
            {
              "exercise_id": "farmer-carry",
              "name": "Farmer carry",
              "sets": 3,
              "reps": "40m",
              "group": "7"
            },
            {
              "exercise_id": "ankle-pogos-from-week-3-only",
              "name": "Ankle pogos (from Week 3 only)",
              "sets": 4,
              "reps": "15-20",
              "notes": "Low, stiff, quiet. See 'Plyo Ramp' tab.",
              "group": "8"
            }
          ]
        }
      ]
    },
    {
      "week": 4,
      "phase": "PHASE 2 — LOAD & STRENGTH · RPE 7–8.5",
      "note": "Heavy slow resistance, unilateral + frontal plane. Progress load ONLY if morning stiffness stayed flat.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "Pogos 4 x 20. Double-leg box drop from 20cm, stick the landing, 3 x 5.",
              "notes": "Landing mechanics. Knees track over toes, no valgus.",
              "group": "PLYO"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 4,
              "reps": "6 (heavier)",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 4,
              "reps": "10 heavy",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 4,
              "reps": "8",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 4,
              "reps": "10/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 4,
              "reps": "6-8",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 3,
              "reps": "12/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 4,
              "reps": "6",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 3,
              "reps": "25",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 3,
              "reps": "8/side heavier",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 4,
              "reps": "8/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "nordic-curl-assisted-band-or-razor-curl",
              "name": "Nordic curl (assisted/band) or razor curl",
              "sets": 3,
              "reps": "6",
              "notes": "Eccentric focus. OMIT in Week 7.",
              "group": "C1"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "8/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 3,
              "reps": "30m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 3,
              "reps": "10/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session C",
          "focus": "Unilateral + Frontal Plane",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "Pogos 4 x 20. Double-leg box drop from 20cm, stick the landing, 3 x 5.",
              "notes": "Landing mechanics. Knees track over toes, no valgus.",
              "group": "PLYO"
            },
            {
              "exercise_id": "rear-foot-elevated-split-squat",
              "name": "Rear-foot elevated split squat",
              "sets": 4,
              "reps": "6/side loaded",
              "tempo": "3-1-1",
              "notes": "Front shin travels - this is a knee-dominant variation on purpose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise-heavy",
              "name": "Seated calf raise - HEAVY",
              "sets": 4,
              "reps": "8 heaviest",
              "notes": "3s eccentric. Heaviest calf exposure of the week.",
              "group": "A2"
            },
            {
              "exercise_id": "lateral-step-down-15-25cm-box",
              "name": "Lateral step-down (15-25cm box)",
              "sets": 3,
              "reps": "10/side",
              "notes": "Slow, controlled. Frontal-plane control - the MCL work.",
              "group": "B1"
            },
            {
              "exercise_id": "incline-db-press",
              "name": "Incline DB press",
              "sets": 4,
              "reps": "8",
              "notes": "45 deg bench.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-calf-raise-double-leg-burnout",
              "name": "Standing calf raise, double leg - burnout",
              "sets": 3,
              "reps": "20",
              "notes": "Blood flow / volume finisher. Full ROM, no bouncing.",
              "group": "C1"
            },
            {
              "exercise_id": "seated-cable-row-face-pulls-superset",
              "name": "Seated cable row + face pulls (superset)",
              "sets": 3,
              "reps": "12 + 3 x 15",
              "notes": "Upper back + posterior shoulder.",
              "group": "C2"
            },
            {
              "exercise_id": "side-plank",
              "name": "Side plank",
              "sets": 3,
              "reps": "40s/side",
              "notes": "Anti-lateral flexion.",
              "group": "D1"
            },
            {
              "exercise_id": "ab-wheel-or-body-saw",
              "name": "Ab wheel or body saw",
              "sets": 3,
              "reps": "10",
              "notes": "Anti-extension. Stop the set when the low back gives.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session D",
          "focus": "Optional Low-CNS",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "daily-tendon-protocol-full",
              "name": "Daily tendon protocol (full)",
              "reps": "as prescribed",
              "notes": "See 'Daily Isometrics' tab.",
              "group": "1"
            },
            {
              "exercise_id": "leg-press-calf-raise",
              "name": "Leg press calf raise",
              "sets": 4,
              "reps": "15",
              "notes": "2s pause at the top.",
              "group": "2"
            },
            {
              "exercise_id": "single-leg-standing-calf-raise-bodyweigh",
              "name": "Single-leg standing calf raise (bodyweight)",
              "sets": 3,
              "reps": "15/side",
              "notes": "High quality only - stop when ROM drops.",
              "group": "3"
            },
            {
              "exercise_id": "tibialis-raise",
              "name": "Tibialis raise",
              "sets": 3,
              "reps": "25",
              "group": "4"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "group": "5"
            },
            {
              "exercise_id": "push-ups-and-band-pull-aparts",
              "name": "Push-ups AND band pull-aparts",
              "reps": "3 sets + 3 x 20",
              "notes": "Push-ups: AMRAP minus 2.",
              "group": "6"
            },
            {
              "exercise_id": "farmer-carry",
              "name": "Farmer carry",
              "sets": 3,
              "reps": "40m",
              "group": "7"
            },
            {
              "exercise_id": "ankle-pogos-from-week-3-only",
              "name": "Ankle pogos (from Week 3 only)",
              "sets": 4,
              "reps": "15-20",
              "notes": "Low, stiff, quiet. See 'Plyo Ramp' tab.",
              "group": "8"
            }
          ]
        }
      ]
    },
    {
      "week": 5,
      "phase": "PHASE 2 — LOAD & STRENGTH · RPE 7–8.5",
      "note": "Heavy slow resistance, unilateral + frontal plane. Progress load ONLY if morning stiffness stayed flat.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "Double-leg broad jump 3 x 4. Single-leg pogos 3 x 10/side. Skater bounds to stick 3 x 6/side.",
              "notes": "First single-leg exposure. Skater bounds are the MCL-relevant drill.",
              "group": "PLYO"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 4,
              "reps": "6 (heavier)",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 4,
              "reps": "10 heavy",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 4,
              "reps": "8",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 4,
              "reps": "10/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 4,
              "reps": "6-8",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 3,
              "reps": "12/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 4,
              "reps": "6",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 3,
              "reps": "25",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 3,
              "reps": "8/side heavier",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 4,
              "reps": "8/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "nordic-curl-assisted-band-or-razor-curl",
              "name": "Nordic curl (assisted/band) or razor curl",
              "sets": 3,
              "reps": "6",
              "notes": "Eccentric focus. OMIT in Week 7.",
              "group": "C1"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "8/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 3,
              "reps": "30m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 3,
              "reps": "10/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session C",
          "focus": "Unilateral + Frontal Plane",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "Double-leg broad jump 3 x 4. Single-leg pogos 3 x 10/side. Skater bounds to stick 3 x 6/side.",
              "notes": "First single-leg exposure. Skater bounds are the MCL-relevant drill.",
              "group": "PLYO"
            },
            {
              "exercise_id": "rear-foot-elevated-split-squat",
              "name": "Rear-foot elevated split squat",
              "sets": 4,
              "reps": "6/side loaded",
              "tempo": "3-1-1",
              "notes": "Front shin travels - this is a knee-dominant variation on purpose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise-heavy",
              "name": "Seated calf raise - HEAVY",
              "sets": 4,
              "reps": "8 heaviest",
              "notes": "3s eccentric. Heaviest calf exposure of the week.",
              "group": "A2"
            },
            {
              "exercise_id": "lateral-step-down-15-25cm-box",
              "name": "Lateral step-down (15-25cm box)",
              "sets": 3,
              "reps": "10/side",
              "notes": "Slow, controlled. Frontal-plane control - the MCL work.",
              "group": "B1"
            },
            {
              "exercise_id": "incline-db-press",
              "name": "Incline DB press",
              "sets": 4,
              "reps": "8",
              "notes": "45 deg bench.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-calf-raise-double-leg-burnout",
              "name": "Standing calf raise, double leg - burnout",
              "sets": 3,
              "reps": "20",
              "notes": "Blood flow / volume finisher. Full ROM, no bouncing.",
              "group": "C1"
            },
            {
              "exercise_id": "seated-cable-row-face-pulls-superset",
              "name": "Seated cable row + face pulls (superset)",
              "sets": 3,
              "reps": "12 + 3 x 15",
              "notes": "Upper back + posterior shoulder.",
              "group": "C2"
            },
            {
              "exercise_id": "side-plank",
              "name": "Side plank",
              "sets": 3,
              "reps": "40s/side",
              "notes": "Anti-lateral flexion.",
              "group": "D1"
            },
            {
              "exercise_id": "ab-wheel-or-body-saw",
              "name": "Ab wheel or body saw",
              "sets": 3,
              "reps": "10",
              "notes": "Anti-extension. Stop the set when the low back gives.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session D",
          "focus": "Optional Low-CNS",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "daily-tendon-protocol-full",
              "name": "Daily tendon protocol (full)",
              "reps": "as prescribed",
              "notes": "See 'Daily Isometrics' tab.",
              "group": "1"
            },
            {
              "exercise_id": "leg-press-calf-raise",
              "name": "Leg press calf raise",
              "sets": 4,
              "reps": "15",
              "notes": "2s pause at the top.",
              "group": "2"
            },
            {
              "exercise_id": "single-leg-standing-calf-raise-bodyweigh",
              "name": "Single-leg standing calf raise (bodyweight)",
              "sets": 3,
              "reps": "15/side",
              "notes": "High quality only - stop when ROM drops.",
              "group": "3"
            },
            {
              "exercise_id": "tibialis-raise",
              "name": "Tibialis raise",
              "sets": 3,
              "reps": "25",
              "group": "4"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "group": "5"
            },
            {
              "exercise_id": "push-ups-and-band-pull-aparts",
              "name": "Push-ups AND band pull-aparts",
              "reps": "3 sets + 3 x 20",
              "notes": "Push-ups: AMRAP minus 2.",
              "group": "6"
            },
            {
              "exercise_id": "farmer-carry",
              "name": "Farmer carry",
              "sets": 3,
              "reps": "40m",
              "group": "7"
            },
            {
              "exercise_id": "ankle-pogos-from-week-3-only",
              "name": "Ankle pogos (from Week 3 only)",
              "sets": 4,
              "reps": "15-20",
              "notes": "Low, stiff, quiet. See 'Plyo Ramp' tab.",
              "group": "8"
            }
          ]
        }
      ]
    },
    {
      "week": 6,
      "phase": "PHASE 3 — EXPRESS & SHARPEN · RPE 8",
      "note": "Volume drops, intent rises. Full power expression.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "CMJ max intent 4 x 3. Single-leg bounds 3 x 5/side. Approach jump to rim 4 x 3.",
              "notes": "Full intent. Long rest between sets - this is a power day, not conditioning.",
              "group": "PLYO"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 4,
              "reps": "4",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 3,
              "reps": "10",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 4,
              "reps": "6",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 3,
              "reps": "8/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 4,
              "reps": "5",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 3,
              "reps": "10/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 4,
              "reps": "4",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 3,
              "reps": "20",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 3,
              "reps": "6/side",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 4,
              "reps": "6/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "nordic-curl-assisted-band-or-razor-curl",
              "name": "Nordic curl (assisted/band) or razor curl",
              "sets": 3,
              "reps": "4",
              "notes": "Eccentric focus. OMIT in Week 7.",
              "group": "C1"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "6/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 2,
              "reps": "30m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 3,
              "reps": "8/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session C",
          "focus": "Unilateral + Frontal Plane",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "CMJ max intent 4 x 3. Single-leg bounds 3 x 5/side. Approach jump to rim 4 x 3.",
              "notes": "Full intent. Long rest between sets - this is a power day, not conditioning.",
              "group": "PLYO"
            },
            {
              "exercise_id": "rear-foot-elevated-split-squat",
              "name": "Rear-foot elevated split squat",
              "sets": 3,
              "reps": "5/side",
              "tempo": "3-1-1",
              "notes": "Front shin travels - this is a knee-dominant variation on purpose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise-heavy",
              "name": "Seated calf raise - HEAVY",
              "sets": 3,
              "reps": "8",
              "notes": "3s eccentric. Heaviest calf exposure of the week.",
              "group": "A2"
            },
            {
              "exercise_id": "lateral-step-down-15-25cm-box",
              "name": "Lateral step-down (15-25cm box)",
              "sets": 3,
              "reps": "8/side",
              "notes": "Slow, controlled. Frontal-plane control - the MCL work.",
              "group": "B1"
            },
            {
              "exercise_id": "incline-db-press",
              "name": "Incline DB press",
              "sets": 4,
              "reps": "6",
              "notes": "45 deg bench.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-calf-raise-double-leg-burnout",
              "name": "Standing calf raise, double leg - burnout",
              "sets": 2,
              "reps": "15",
              "notes": "Blood flow / volume finisher. Full ROM, no bouncing.",
              "group": "C1"
            },
            {
              "exercise_id": "seated-cable-row-face-pulls-superset",
              "name": "Seated cable row + face pulls (superset)",
              "sets": 3,
              "reps": "10 + 3 x 15",
              "notes": "Upper back + posterior shoulder.",
              "group": "C2"
            },
            {
              "exercise_id": "side-plank",
              "name": "Side plank",
              "sets": 3,
              "reps": "30s/side",
              "notes": "Anti-lateral flexion.",
              "group": "D1"
            },
            {
              "exercise_id": "ab-wheel-or-body-saw",
              "name": "Ab wheel or body saw",
              "sets": 3,
              "reps": "8",
              "notes": "Anti-extension. Stop the set when the low back gives.",
              "group": "D2"
            }
          ]
        },
        {
          "label": "Session D",
          "focus": "Optional Low-CNS",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "daily-tendon-protocol-full",
              "name": "Daily tendon protocol (full)",
              "reps": "as prescribed",
              "notes": "See 'Daily Isometrics' tab.",
              "group": "1"
            },
            {
              "exercise_id": "leg-press-calf-raise",
              "name": "Leg press calf raise",
              "sets": 3,
              "reps": "15",
              "notes": "2s pause at the top.",
              "group": "2"
            },
            {
              "exercise_id": "single-leg-standing-calf-raise-bodyweigh",
              "name": "Single-leg standing calf raise (bodyweight)",
              "sets": 3,
              "reps": "12/side",
              "notes": "High quality only - stop when ROM drops.",
              "group": "3"
            },
            {
              "exercise_id": "tibialis-raise",
              "name": "Tibialis raise",
              "sets": 3,
              "reps": "20",
              "group": "4"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 3,
              "reps": "30s/side",
              "group": "5"
            },
            {
              "exercise_id": "push-ups-and-band-pull-aparts",
              "name": "Push-ups AND band pull-aparts",
              "reps": "2 sets + 3 x 20",
              "notes": "Push-ups: AMRAP minus 2.",
              "group": "6"
            },
            {
              "exercise_id": "farmer-carry",
              "name": "Farmer carry",
              "sets": 2,
              "reps": "40m",
              "group": "7"
            },
            {
              "exercise_id": "ankle-pogos-from-week-3-only",
              "name": "Ankle pogos (from Week 3 only)",
              "sets": 4,
              "reps": "20",
              "notes": "Low, stiff, quiet. See 'Plyo Ramp' tab.",
              "group": "8"
            }
          ]
        }
      ]
    },
    {
      "week": 7,
      "phase": "WEEK 7 — TAPER / DEPARTURE · RPE 6",
      "note": "Two short sessions only (A + B). Keep daily isometrics through flight days. No PBs, no new exercises.",
      "days": [
        {
          "label": "Daily",
          "focus": "Tendon Protocol · 5–6 days/week",
          "exercises": [
            {
              "exercise_id": "spanish-squat-isometric",
              "name": "Spanish squat isometric",
              "sets": 5,
              "reps": "45s @ ~70% effort, 2 min rest",
              "notes": "Band behind knees, shins vertical, 70-90 deg knee flexion. This is the primary analgesic dose."
            },
            {
              "exercise_id": "or-isometric-leg-extension",
              "name": "OR Isometric leg extension",
              "sets": 5,
              "reps": "45s @ 70% MVIC at 60 deg",
              "notes": "Use if no Spanish squat setup, or if he needs more isolated quad drive."
            },
            {
              "exercise_id": "isometric-single-leg-calf-raise-hold",
              "name": "Isometric single-leg calf raise hold",
              "sets": 3,
              "reps": "30s/side, top position",
              "notes": "From Week 1. Ankle stiffness + Achilles insurance."
            }
          ]
        },
        {
          "label": "Session A",
          "focus": "Squat + Horizontal Push",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "plyo-ramp",
              "name": "Plyometric Ramp",
              "reps": "CMJ 3 x 3. Pogos 2 x 15.",
              "notes": "Keep the nervous system sharp. Add nothing.",
              "group": "PLYO"
            },
            {
              "exercise_id": "goblet-or-front-squat",
              "name": "Goblet or Front squat",
              "sets": 3,
              "reps": "5 light",
              "tempo": "3-1-3",
              "notes": "Heavy slow resistance - the tendon remodelling dose.",
              "group": "A1"
            },
            {
              "exercise_id": "seated-calf-raise",
              "name": "Seated calf raise",
              "sets": 3,
              "reps": "10",
              "notes": "3s down, 2s hold at top. Bent knee = soleus. Priority lift.",
              "group": "A2"
            },
            {
              "exercise_id": "db-bench-press",
              "name": "DB bench press",
              "sets": 3,
              "reps": "8",
              "notes": "Full ROM, controlled eccentric.",
              "group": "B1"
            },
            {
              "exercise_id": "copenhagen-plank",
              "name": "Copenhagen plank",
              "sets": 2,
              "reps": "20s/side",
              "notes": "Short lever Wk1-3, long lever from Wk4. Adductor / MCL insurance.",
              "group": "B2"
            },
            {
              "exercise_id": "standing-single-leg-calf-raise-db-loaded",
              "name": "Standing single-leg calf raise (DB loaded)",
              "sets": 2,
              "reps": "10/side",
              "notes": "3s down, full stretch at bottom. Gastroc bias.",
              "group": "C1"
            },
            {
              "exercise_id": "chin-up-or-lat-pulldown",
              "name": "Chin-up or lat pulldown",
              "sets": 3,
              "reps": "6",
              "notes": "Add load once he clears 10 clean reps.",
              "group": "C2"
            },
            {
              "exercise_id": "pallof-press",
              "name": "Pallof press",
              "sets": 2,
              "reps": "10/side",
              "notes": "Anti-rotation. Ribs down, no hip shift.",
              "group": "D"
            }
          ]
        },
        {
          "label": "Session B",
          "focus": "Hinge + Pull",
          "exercises": [
            {
              "exercise_id": "warm-up",
              "name": "Warm-up",
              "reps": "~10 min",
              "is_warmup": true,
              "notes": "Bike/rower 3 min · ankle rocks + calf/soleus · glute bridge, banded lateral walk, 90/90 · slow-tempo BW squat · Spanish squat isometrics"
            },
            {
              "exercise_id": "romanian-deadlift",
              "name": "Romanian deadlift",
              "sets": 3,
              "reps": "5 light",
              "tempo": "3-0-1",
              "notes": "Hamstring length under load.",
              "group": "A1"
            },
            {
              "exercise_id": "tibialis-raise-wall-or-dorsiflexion-mach",
              "name": "Tibialis raise (wall or dorsiflexion machine)",
              "sets": 2,
              "reps": "20",
              "notes": "Slow, full ROM. Shin health + ankle balance.",
              "group": "A2"
            },
            {
              "exercise_id": "single-leg-rdl-db",
              "name": "Single-leg RDL (DB)",
              "sets": 2,
              "reps": "8/side",
              "notes": "Hip control, no rotation through the pelvis.",
              "group": "B1"
            },
            {
              "exercise_id": "1-arm-db-row",
              "name": "1-arm DB row",
              "sets": 3,
              "reps": "8/side",
              "notes": "Full stretch, no torso rotation.",
              "group": "B2"
            },
            {
              "exercise_id": "half-kneeling-landmine-press",
              "name": "Half-kneeling landmine press",
              "sets": 3,
              "reps": "8/side",
              "notes": "Shoulder-friendly overhead pattern for a basketball athlete.",
              "group": "C2"
            },
            {
              "exercise_id": "suitcase-carry",
              "name": "Suitcase carry",
              "sets": 2,
              "reps": "20m/side",
              "notes": "Anti-lateral flexion. Stay tall, don't lean away.",
              "group": "D1"
            },
            {
              "exercise_id": "dead-bug",
              "name": "Dead bug",
              "sets": 2,
              "reps": "8/side",
              "notes": "Slow, exhale on the reach. Low back stays flat.",
              "group": "D2"
            }
          ]
        }
      ]
    }
  ]
}

export const preUsaAthlete: Athlete = {
  id: 'usa1',
  name: 'Tyrese Walker',
  meta: 'Week 3 · Pre-USA Prep',
  currentWeek: 3,
  program: preUsaProgram,
}
